
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { PlusCircle, Trash2 } from 'lucide-react';
import type { Application, HealthPlan } from '@/types/database.types';

interface HealthPlansProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const HealthPlans = ({
  register,
  watch,
  setValue,
  data
}: HealthPlansProps) => {
  const healthPlans = watch('health_plans') || [];
  
  const addHealthPlan = () => {
    const newPlan: HealthPlan = {
      plan_name: '',
      plan_type: '',
      carrier: '',
      annual_premium: '',
      employee_contribution: '',
      employer_contribution: ''
    };
    
    setValue('health_plans', [...healthPlans, newPlan]);
  };
  
  const removeHealthPlan = (index: number) => {
    const updatedPlans = [...healthPlans];
    updatedPlans.splice(index, 1);
    setValue('health_plans', updatedPlans);
  };
  
  const updateHealthPlan = (index: number, field: keyof HealthPlan, value: string) => {
    const updatedPlans = [...healthPlans];
    if (updatedPlans[index]) {
      updatedPlans[index] = { 
        ...updatedPlans[index], 
        [field]: value 
      };
      setValue('health_plans', updatedPlans);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Health Plans</h3>
        <Button 
          onClick={addHealthPlan} 
          variant="outline" 
          size="sm"
          type="button"
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Plan
        </Button>
      </div>
      <Separator className="my-2" />
      
      {healthPlans.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          No health plans added yet. Click "Add Plan" to begin.
        </div>
      ) : (
        <div className="space-y-6">
          {healthPlans.map((plan, index) => (
            <div key={index} className="p-4 border rounded-md bg-background/50 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Plan {index + 1}</h4>
                <Button
                  onClick={() => removeHealthPlan(index)}
                  variant="ghost"
                  size="sm"
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`plan_name_${index}`}>Plan Name</Label>
                  <Input
                    id={`plan_name_${index}`}
                    value={plan.plan_name}
                    onChange={(e) => updateHealthPlan(index, 'plan_name', e.target.value)}
                    placeholder="Enter plan name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`plan_type_${index}`}>Plan Type</Label>
                  <Input
                    id={`plan_type_${index}`}
                    value={plan.plan_type}
                    onChange={(e) => updateHealthPlan(index, 'plan_type', e.target.value)}
                    placeholder="PPO, HMO, etc."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`carrier_${index}`}>Carrier</Label>
                  <Input
                    id={`carrier_${index}`}
                    value={plan.carrier}
                    onChange={(e) => updateHealthPlan(index, 'carrier', e.target.value)}
                    placeholder="Insurance carrier"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`annual_premium_${index}`}>Annual Premium</Label>
                  <Input
                    id={`annual_premium_${index}`}
                    value={plan.annual_premium}
                    onChange={(e) => updateHealthPlan(index, 'annual_premium', e.target.value)}
                    placeholder="Total annual premium"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`employee_contribution_${index}`}>Employee Contribution</Label>
                  <Input
                    id={`employee_contribution_${index}`}
                    value={plan.employee_contribution}
                    onChange={(e) => updateHealthPlan(index, 'employee_contribution', e.target.value)}
                    placeholder="Employee contribution"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`employer_contribution_${index}`}>Employer Contribution</Label>
                  <Input
                    id={`employer_contribution_${index}`}
                    value={plan.employer_contribution}
                    onChange={(e) => updateHealthPlan(index, 'employer_contribution', e.target.value)}
                    placeholder="Employer contribution"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
